import { Dispatch, FC, SetStateAction, useMemo } from 'react';
import { ColumnFilter, ColumnFilters, Pagination, RequestStatusEnum, UnknownObject } from '@shared/types';
import { MRT_ColumnDef, MRT_Row, MRT_RowData, MRT_TableInstance } from 'material-react-table';
import OverflowTooltip from '@shared/components/ui/OverflowTooltip';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import dayjs from 'dayjs';
import CustomTable from '@shared/components/others/CustomTable/CustomTable';
import { useSelector } from 'react-redux';
import UtmsPagination from '@shared/components/ui/UtmsPagination';
import EmptyList from '@shared/components/others/EmptyList/EmptyList';
import PcapFilesListToolbar, { PcapFilesListToolbarProps } from './PcapFilesListToolbar';
import { PcapFile, PcapFileWithCps } from '../types';
import { selectGetPcapFilesStatus, selectPcapFilesCount } from '../store/selectors';
import { toTitleCase } from '@shared/utils/utilsOld';

interface PcapFilesTableProps {
    pcapFiles: PcapFile[];
    pagination: Pagination;
    rowSelection: UnknownObject;
    setRowSelection: Dispatch<SetStateAction<UnknownObject>>;
    globalFilter: string;
    setGlobalFilter: Dispatch<SetStateAction<string>>;
    showColumnFilters: boolean;
    setShowColumnFilters: Dispatch<SetStateAction<boolean>>;
    sorting: { id: string; desc: boolean }[];
    setSorting: Dispatch<SetStateAction<{ id: string; desc: boolean }>>;
    columnFilters: ColumnFilters;
    setColumnFilters: Dispatch<SetStateAction<ColumnFilters>>;
    isFilteringApplied?: boolean;
    callbacks: {
        handleChangePagination: (newPagination: Pagination) => void;
        handleClearAllFilters: () => void;
    };
    toolbarProps: Omit<PcapFilesListToolbarProps, 'table'>;
    dialogMode?: boolean;
    pcapFilesUsed?: PcapFileWithCps[];
}

const PcapFilesTable: FC<PcapFilesTableProps> = ({
    pcapFiles,
    rowSelection,
    setRowSelection,
    pagination,
    globalFilter,
    setGlobalFilter,
    showColumnFilters,
    setShowColumnFilters,
    sorting,
    setSorting,
    columnFilters,
    setColumnFilters,
    isFilteringApplied = true,
    callbacks,
    toolbarProps,
    dialogMode,
    pcapFilesUsed,
}) => {
    const { t } = useTranslation();
    const getPcapFilesStatus = useSelector(selectGetPcapFilesStatus);
    const pcapFilesCount = useSelector(selectPcapFilesCount);
    // const users = useSelector(selectUsers);

    const { handleChangePagination, handleClearAllFilters } = callbacks;

    // const handleChangeUserFilter = useCallback(
    //     (newValue: User['pk']) => {
    //         setColumnFilters &&
    //             setColumnFilters((filters) => [
    //                 ...filters.filter((filter: ColumnFilter) => filter.id !== 'created_by_user'),
    //                 { id: 'created_by_user', value: String(newValue) },
    //             ]);
    //     },
    //     [setColumnFilters],
    // );

    // const handleClearUserFilter = useCallback(() => {
    //     setColumnFilters &&
    //         setColumnFilters((filters) => [...filters.filter((filter: any) => filter.id !== 'created_by_user')]);
    // }, [setColumnFilters]);

    const columns = useMemo<MRT_ColumnDef<PcapFile>[]>(
        () => [
            {
                accessorKey: 'pk',
                header: 'ID',
                // size: 90,
                Cell: ({ cell }) => (
                    <OverflowTooltip title={cell.getValue<string>()}>{cell.getValue<string>()}</OverflowTooltip>
                ),
            },
            {
                accessorKey: 'name',
                header: t('name'),
                // size: 150,
                Cell: ({ cell }) => {
                    const name = String(cell.getValue());

                    return (
                        <OverflowTooltip title={name} maxLines={6}>
                            {name}
                        </OverflowTooltip>
                    );
                },
            },
            {
                accessorKey: 'description',
                header: t('description'),
                // size: 165,
                enableSorting: false,
                Cell: ({ cell }) => {
                    const value = cell.getValue();
                    return value ? (
                        <OverflowTooltip title={String(value)} maxLines={6}>
                            <Box
                                sx={{
                                    // maxWidth: cell.column.getSize() - 32,
                                    maxHeight: 120,
                                }}
                            >
                                {String(value)}
                            </Box>
                        </OverflowTooltip>
                    ) : (
                        '—'
                    );
                },
            },
            // TODO
            {
                accessorKey: 'created_at',
                header: toTitleCase(t('created_at')),
                // size: 127,
                Cell: ({ cell }) => {
                    const date = cell.getValue<string>();
                    return (
                        <Typography component={'span'}>
                            {date ? dayjs(date).format('DD.MM.YYYY HH:mm ') : t('no_info')}
                        </Typography>
                    );
                },
                filterVariant: 'date',
            },
            // {
            //     accessorKey: 'created_by_user',
            //     header: t('created_by'),
            //     size: 171,
            //     enableSorting: false,
            //     Cell: ({ cell }) => <UserTableView userId={cell.getValue()} />,
            //     filterVariant: 'autocomplete',
            //     // @ts-ignore
            //     filterSelectOptions: users.map((item) => ({
            //         label: `${item.first_name} ${item.last_name}`,
            //         value: item.pk,
            //     })),
            //     Filter: () => (
            //         <UserSelector
            //             sx={{ width: '100%' }}
            //             value={userId}
            //             onChange={(_event: any, newOption: User | null) => {
            //                 newOption ? handleChangeUserFilter(newOption.pk) : handleClearUserFilter();
            //             }}
            //             renderOption={(props: any, option: { pk: Key | null | undefined }) => (
            //                 //@ts-ignore
            //                 <AuthorAndEditorInfo
            //                     key={option.pk}
            //                     createdUserId={option.pk}
            //                     hiddenLabel
            //                     hiddenDate
            //                     hiddenAvatar
            //                     userContainerProps={{
            //                         sx: {
            //                             border: 'none',
            //                             gap: '2px',
            //                             margin: 0,
            //                             width: '100%',
            //                         },
            //                         ...props,
            //                     }}
            //                     showPopoverInfo={false}
            //                     style={{ margin: 0 }}
            //                 />
            //             )}
            //             TextFieldProps={{
            //                 variant: 'standard',
            //                 label: null,
            //             }}
            //             inputHeight={34}
            //         />
            //     ),
            // },

            // {
            //     accessorKey: 'last_update_time',
            //     header: toTitleCase(t('modified_at')),
            //     size: 127,
            //     Cell: ({ cell }) => {
            //         const date = cell.getValue<string>();
            //         return (
            //             <Typography component={'span'}>
            //                 {date ? dayjs(date).format('DD.MM.YYYY HH:mm ') : t('no_info')}
            //             </Typography>
            //         );
            //     },
            //     filterVariant: 'date',
            // },
            // {
            //     accessorKey: 'last_update_by_user',
            //     header: t('modified_by'),
            //     size: 171,
            //     enableSorting: false,
            //     Cell: ({ cell }) => <UserTableView userId={cell.getValue()} />,
            //     filterVariant: 'autocomplete',
            //     // @ts-ignore
            //     filterSelectOptions: users.map((item) => ({
            //         label: `${item.first_name} ${item.last_name}`,
            //         value: item.pk,
            //     })),
            //     Filter: () => (
            //         <UserSelector
            //             sx={{ width: '100%' }}
            //             value={userId}
            //             onChange={(_event: any, newOption: User | null) => {
            //                 newOption ? handleChangeUserFilter(newOption.pk) : handleClearUserFilter();
            //             }}
            //             renderOption={(props: any, option: { pk: Key | null | undefined }) => (
            //                 //@ts-ignore
            //                 <AuthorAndEditorInfo
            //                     key={option.pk}
            //                     createdUserId={option.pk}
            //                     hiddenLabel
            //                     hiddenDate
            //                     hiddenAvatar
            //                     userContainerProps={{
            //                         sx: {
            //                             border: 'none',
            //                             gap: '2px',
            //                             margin: 0,
            //                             width: '100%',
            //                         },
            //                         ...props,
            //                     }}
            //                     showPopoverInfo={false}
            //                     style={{ margin: 0 }}
            //                 />
            //             )}
            //             TextFieldProps={{
            //                 variant: 'standard',
            //                 label: null,
            //             }}
            //             inputHeight={34}
            //         />
            //     ),
            // },
        ],
        [t],
    );

    return (
        // @ts-ignore
        <CustomTable
            tableId="pcap-files-table"
            columns={columns}
            data={pcapFiles}
            toolbar={(table: MRT_TableInstance<MRT_RowData>) => (
                <PcapFilesListToolbar {...toolbarProps} table={table} />
            )}
            dialogMode={dialogMode}
            variant="outlined"
            state={{
                showSkeletons: getPcapFilesStatus === RequestStatusEnum.Loading,
                showColumnFilters,
                rowSelection,
                globalFilter,
                pagination,
                sorting,
                columnFilters: columnFilters.map((columnFilter: ColumnFilter) => {
                    if (columnFilter.id === 'created_at') {
                        columnFilter.value = dayjs(columnFilter.value) as unknown as ColumnFilter['value'];
                    }

                    return columnFilter;
                }),
            }}
            enableRowSelection={dialogMode}
            onRowSelectionChange={setRowSelection}
            onShowColumnFiltersChange={setShowColumnFilters}
            onGlobalFilterChange={setGlobalFilter}
            onPaginationChange={handleChangePagination}
            rowCount={pcapFilesCount}
            onSortingChange={setSorting}
            onColumnFiltersChange={setColumnFilters}
            muiTableBodyRowProps={({ row }: { row: MRT_Row<MRT_RowData> }) => ({
                onClick: () =>
                    getPcapFilesStatus === RequestStatusEnum.Success
                        ? dialogMode && pcapFilesUsed
                            ? setRowSelection((prev) => {
                                  const newRowSelection = structuredClone(prev);
                                  if (newRowSelection[row.id]) {
                                      if (!pcapFilesUsed.find((item) => item.pk === Number(row.id))) {
                                          delete newRowSelection[row.id];
                                      }
                                  } else {
                                      newRowSelection[row.id] = true;
                                  }
                                  return newRowSelection;
                              })
                            : setRowSelection((prev) => ({
                                  [row.id]: !prev[row.id],
                              }))
                        : undefined,
                selected: rowSelection[row.id],
            })}
            enablePagination={false}
            muiBottomToolbarProps={{
                sx: {
                    minHeight: 0,
                    boxShadow: 0,
                    backgroundColor: 'inherit',
                },
            }}
            //@ts-ignore
            defaultColumn={{
                muiTableBodyCellProps: {
                    sx: {
                        alignItems: 'flex-start',
                        fontSize: 14,
                        whiteSpace: 'wrap',
                        ':has(> .MuiCheckbox-root)': {
                            pt: '4px',
                        },
                        p: '8px 16px',
                    },
                },
            }}
            enableSelectAll={false}
            renderBottomToolbar={() => (
                <UtmsPagination
                    view="table"
                    pagination={pagination}
                    count={pcapFilesCount}
                    handleChangePagination={handleChangePagination}
                />
            )}
            muiTableContainerProps={{
                sx: {
                    maxHeight: 'calc(100% - 7rem - 3px)', // 10rem = 3.5rem header + 3.5rem footer height + 3rem toolbar
                    scrollbarGutter: 'stable',
                },
            }}
            renderEmptyRowsFallback={() => (
                //@ts-ignore
                <EmptyList
                    getListStatus={getPcapFilesStatus}
                    count={pcapFilesCount}
                    isFilteringApplied={isFilteringApplied}
                    handleClearAllFilters={handleClearAllFilters}
                />
            )}
            muiSelectCheckboxProps={({ row }: { row: MRT_Row<MRT_RowData> }) => ({
                disabled: pcapFilesUsed?.find((item) => item.pk === Number(row.id)),
            })}
            muiTablePaperProps={undefined}
        />
    );
};

export default PcapFilesTable;
